/**
 * Pure operations over the Presentation model (`office-io.ts`).
 *
 * Same philosophy as `workbook-ops`: immutable transforms, trivially
 * unit-testable, consumed by the slides editor through Solid signals. The
 * model is deliberately the pptx/odp subset the Rust crate round-trips:
 * ordered slides, each with an optional title and body text lines.
 */
import type { Presentation, Slide } from "./office-io";

export function emptySlide(): Slide {
  return { body: [] };
}

export function emptyPresentation(): Presentation {
  return { slides: [emptySlide()] };
}

/** Inserts a blank slide AFTER `index` (or at the end when out of range). */
export function addSlide(deck: Presentation, index?: number): Presentation {
  const slides = [...deck.slides];
  const at = index === undefined ? slides.length : Math.min(index + 1, slides.length);
  slides.splice(at, 0, emptySlide());
  return { slides };
}

/** Removes the slide; a deck always keeps at least one (blank) slide. */
export function removeSlide(deck: Presentation, index: number): Presentation {
  if (index < 0 || index >= deck.slides.length) {
    return deck;
  }
  const slides = deck.slides.filter((_, i) => i !== index);
  return { slides: slides.length > 0 ? slides : [emptySlide()] };
}

/** Moves a slide by `delta` positions (clamped). Returns the same deck when no-op. */
export function moveSlide(deck: Presentation, index: number, delta: number): Presentation {
  const target = index + delta;
  if (index < 0 || index >= deck.slides.length) {
    return deck;
  }
  if (target < 0 || target >= deck.slides.length) {
    return deck;
  }
  const slides = [...deck.slides];
  const [slide] = slides.splice(index, 1);
  slides.splice(target, 0, slide!);
  return { slides };
}

export function setSlideTitle(deck: Presentation, index: number, title: string): Presentation {
  const slide = deck.slides[index];
  if (!slide) {
    return deck;
  }
  const slides = [...deck.slides];
  const trimmed = title.trim();
  slides[index] = { ...slide, title: trimmed === "" ? undefined : title };
  return { slides };
}

/** Body edited as a single textarea: one line per body entry. */
export function setSlideBodyText(deck: Presentation, index: number, text: string): Presentation {
  const slide = deck.slides[index];
  if (!slide) {
    return deck;
  }
  const slides = [...deck.slides];
  slides[index] = { ...slide, body: text === "" ? [] : text.split("\n") };
  return { slides };
}

export function slideBodyText(slide: Slide | undefined): string {
  return slide?.body.join("\n") ?? "";
}

/**
 * Presentation-mode navigation: the slide index after stepping `delta`
 * (clamped to the deck — no wrap-around, an empty deck pins to 0).
 */
export function stepSlideIndex(current: number, delta: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return Math.min(Math.max(current + delta, 0), total - 1);
}
